import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Card,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsIcon from "@mui/icons-material/Collections";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import employeeService from "services/employeeService";
import formsService from "services/formsService";
import { Hub } from "common/models";
import { Employee } from "employee/models";
import { toast } from "react-toastify";
import { Moment } from "moment";

interface FormData {
  dateOfOverlap: Moment | null;
  locationId: number | null;
  employeeId: number | null;
  shiftNumber: string;
  scheduledStartTime: Moment | null;
  scheduledEndTime: Moment | null;
  actualEndTime: Moment | null;
  reasonForOverlap: string;
  authorizationPhotos: File[];
  authorizedOfficialName: string;
  officialTitle: string;
  officialSignature: string;
  signatureDate: Moment | null;
}

interface FormErrors {
  dateOfOverlap?: string;
  locationId?: string;
  employeeId?: string;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  actualEndTime?: string;
  reasonForOverlap?: string;
  authorizedOfficialName?: string;
  officialTitle?: string;
  officialSignature?: string;
  signatureDate?: string;
}

const computeOverlapMinutes = (scheduledEnd: Moment | null, actualEnd: Moment | null): number => {
  if (!scheduledEnd || !actualEnd) return 0;
  const diff = actualEnd.diff(scheduledEnd, "minutes");
  return diff > 0 ? diff : 0;
};

const ShiftOverlapAuthorization = () => {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const signatureRef = useRef<SignatureCanvas>(null);
  const [locations, setLocations] = useState<Hub[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<FormData>({
    dateOfOverlap: null,
    locationId: null,
    employeeId: null,
    shiftNumber: "",
    scheduledStartTime: null,
    scheduledEndTime: null,
    actualEndTime: null,
    reasonForOverlap: "",
    authorizationPhotos: [],
    authorizedOfficialName: "",
    officialTitle: "",
    officialSignature: "",
    signatureDate: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.locationId) {
      loadEmployeesByHub(formData.locationId);
    } else {
      setEmployees([]);
    }
  }, [formData.locationId]);

  const loadData = async () => {
    try {
      const hubsData = await orgDataService.getAllHubs();
      setLocations(hubsData);
    } catch (error) {
      toast.error("Failed to load form data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployeesByHub = async (hubId: number) => {
    setIsLoadingEmployees(true);
    try {
      const result = await employeeService.searchEmployees({
        hubId: hubId,
        status: "activeOnly",
        pagedSearchOptions: { offset: 0, pageSize: 500 },
        sortName: "lastName",
        sortAsc: true,
      } as any);
      setEmployees(result.results || []);
    } catch (error) {
      toast.error("Failed to load employees");
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.dateOfOverlap) newErrors.dateOfOverlap = "Date of shift overlap is required";
    if (formData.locationId === null) newErrors.locationId = "Location is required";
    if (formData.employeeId === null) newErrors.employeeId = "Employee is required";
    if (!formData.scheduledStartTime) newErrors.scheduledStartTime = "Scheduled shift start time is required";
    if (!formData.scheduledEndTime) newErrors.scheduledEndTime = "Scheduled shift end time is required";
    if (!formData.actualEndTime) newErrors.actualEndTime = "Actual shift end time is required";
    if (!formData.reasonForOverlap.trim()) newErrors.reasonForOverlap = "A reason for the shift overlap is required";
    if (!formData.authorizedOfficialName.trim()) newErrors.authorizedOfficialName = "Authorized railroad official name is required";
    if (!formData.officialTitle.trim()) newErrors.officialTitle = "Railroad official title is required";
    if (!formData.officialSignature) newErrors.officialSignature = "Signature is required";
    if (!formData.signatureDate) newErrors.signatureDate = "Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    const selectedLocation = locations.find((l) => l.id === formData.locationId);
    const selectedEmployee = employees.find((e) => e.id === formData.employeeId);

    if (!selectedLocation || !selectedEmployee) {
      toast.error("Please select a valid location and employee");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await formsService.submitShiftOverlapAuthorization(
        {
          dateOfOverlap: formData.dateOfOverlap!.toISOString(),
          locationId: formData.locationId!,
          locationName: selectedLocation.name,
          employeeId: formData.employeeId!,
          employeeName: `${selectedEmployee.lastName}, ${selectedEmployee.firstName}`,
          employeeNumber: selectedEmployee.employeeNumber,
          shiftNumber: formData.shiftNumber,
          scheduledStartTime: formData.scheduledStartTime!.toISOString(),
          scheduledEndTime: formData.scheduledEndTime!.toISOString(),
          actualEndTime: formData.actualEndTime!.toISOString(),
          overlapMinutes: computeOverlapMinutes(formData.scheduledEndTime, formData.actualEndTime),
          reasonForOverlap: formData.reasonForOverlap,
          authorizedOfficialName: formData.authorizedOfficialName,
          officialTitle: formData.officialTitle,
          officialSignature: formData.officialSignature,
          signatureDate: formData.signatureDate!.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        formData.authorizationPhotos
      );

      if (response.success) {
        toast.success(response.message || "Shift overlap authorization submitted successfully");
        navigate("/forms");
      } else {
        toast.error(response.message || "Failed to submit shift overlap authorization");
      }
    } catch (error) {
      toast.error("Failed to submit shift overlap authorization");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === "locationId") {
      setFormData({ ...formData, locationId: value, employeeId: null });
      setErrors({ ...errors, locationId: undefined, employeeId: undefined });
    } else {
      setFormData({ ...formData, [field]: value });
      if (errors[field as keyof FormErrors]) {
        setErrors({ ...errors, [field]: undefined });
      }
    }
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setFormData((prev) => ({ ...prev, officialSignature: signatureData }));
      if (errors.officialSignature) {
        setErrors((prev) => ({ ...prev, officialSignature: undefined }));
      }
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setFormData((prev) => ({ ...prev, officialSignature: "" }));
  };

  const handleFilesSelected = (
    files: FileList | null,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setFormData((prev) => ({
      ...prev,
      authorizationPhotos: [...prev.authorizationPhotos, ...newFiles],
    }));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      authorizationPhotos: prev.authorizationPhotos.filter((_, i) => i !== index),
    }));
  };

  if (isLoading) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  const overlapMinutes = computeOverlapMinutes(formData.scheduledEndTime, formData.actualEndTime);

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Closing - Shift Overlap Authorization
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Authorize a shift overlap at closing
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Info
          </MDTypography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Date of Shift Overlap *"
                value={formData.dateOfOverlap}
                onChange={(value) => handleChange("dateOfOverlap", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.dateOfOverlap,
                    helperText: errors.dateOfOverlap,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.locationId}>
                <InputLabel id="location-label">Location *</InputLabel>
                <Select
                  labelId="location-label"
                  id="locationId"
                  value={formData.locationId || ""}
                  label="Location *"
                  onChange={(e) => handleChange("locationId", e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!errors.employeeId}
                disabled={!formData.locationId || isLoadingEmployees}
              >
                <InputLabel id="employee-label">
                  {isLoadingEmployees ? "Loading employees..." : "Employee Name *"}
                </InputLabel>
                <Select
                  labelId="employee-label"
                  id="employeeId"
                  value={formData.employeeId || ""}
                  label={isLoadingEmployees ? "Loading employees..." : "Employee Name *"}
                  onChange={(e) => handleChange("employeeId", e.target.value)}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.lastName}, {employee.firstName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.employeeId && <FormHelperText>{errors.employeeId}</FormHelperText>}
                {!formData.locationId && !errors.employeeId && (
                  <FormHelperText>Select a location first</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shift Number"
                value={formData.shiftNumber}
                onChange={(e) => handleChange("shiftNumber", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Scheduled Shift Start Time *"
                value={formData.scheduledStartTime}
                onChange={(value) => handleChange("scheduledStartTime", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.scheduledStartTime,
                    helperText: errors.scheduledStartTime,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Scheduled Shift End Time *"
                value={formData.scheduledEndTime}
                onChange={(value) => handleChange("scheduledEndTime", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.scheduledEndTime,
                    helperText: errors.scheduledEndTime,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Actual Shift End Time *"
                value={formData.actualEndTime}
                onChange={(value) => handleChange("actualEndTime", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.actualEndTime,
                    helperText: errors.actualEndTime,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Overlap to be Authorized (minutes)"
                value={overlapMinutes}
                InputProps={{ readOnly: true }}
                disabled
                helperText="Calculated from actual vs. scheduled shift end time"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Describe in detail the reason for the shift overlap *"
                value={formData.reasonForOverlap}
                onChange={(e) => handleChange("reasonForOverlap", e.target.value)}
                error={!!errors.reasonForOverlap}
                helperText={errors.reasonForOverlap}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <MDTypography variant="h6" fontWeight="medium" mb={1}>
            Railroad Authorization
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            The photo below is optional but needs to be used if you are provided a hand written
            authorization form. Please take a clear picture of the form and mark in the signature
            area as &quot;See Attached.&quot;
          </MDTypography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDTypography variant="body2" fontWeight="medium" mb={1}>
                Photo of Hand Written Authorization Form
              </MDTypography>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={(e) => handleFilesSelected(e.target.files, cameraInputRef)}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFilesSelected(e.target.files, galleryInputRef)}
              />
              <Stack direction="row" spacing={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  Take Photo
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<CollectionsIcon />}
                  onClick={() => galleryInputRef.current?.click()}
                >
                  Choose Photos
                </MDButton>
              </Stack>
              {formData.authorizationPhotos.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" mt={2} useFlexGap>
                  {formData.authorizationPhotos.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={file.name}
                      onDelete={() => handleRemoveFile(index)}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Authorized Railroad Official Name *"
                value={formData.authorizedOfficialName}
                onChange={(e) => handleChange("authorizedOfficialName", e.target.value)}
                error={!!errors.authorizedOfficialName}
                helperText={errors.authorizedOfficialName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Railroad Official Title *"
                value={formData.officialTitle}
                onChange={(e) => handleChange("officialTitle", e.target.value)}
                error={!!errors.officialTitle}
                helperText={errors.officialTitle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox>
                <MDTypography variant="caption" color="text">
                  Authorized Railroad Official Name (Signature) *
                </MDTypography>
                <MDBox
                  sx={{
                    border: errors.officialSignature ? "1px solid #f44336" : "1px solid #d2d6da",
                    borderRadius: "8px",
                    position: "relative",
                    backgroundColor: "#fff",
                  }}
                >
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      style: { width: "100%", height: "100px" },
                    }}
                    onEnd={handleSignatureEnd}
                  />
                  <IconButton
                    size="small"
                    onClick={clearSignature}
                    sx={{ position: "absolute", top: 2, right: 2 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </MDBox>
                {errors.officialSignature && (
                  <FormHelperText error>{errors.officialSignature}</FormHelperText>
                )}
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Date *"
                value={formData.signatureDate}
                onChange={(value) => handleChange("signatureDate", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.signatureDate,
                    helperText: errors.signatureDate,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/forms")}
              disabled={isSubmitting}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="dark"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </MDButton>
          </Box>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default ShiftOverlapAuthorization;
